import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import HoursSummary from './HoursSummary';
import '../styles/HoursSummaryPage.css';

const HoursSummaryPage = () => {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const headers = {
                Authorization: `Bearer ${token}`,
            };

            try {
                // Fetch employees, schedules, and requests in parallel
                const [employeesResponse, schedulesResponse, requestsResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/employees`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/schedules`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/requests/company`, { headers })
                ]);

                // Create a mapping between user_ids and employee IDs
                const userIdToEmployeeId = {};
                employeesResponse.data.forEach(employee => {
                    if (employee.user_id) {
                        userIdToEmployeeId[employee.user_id] = employee.id;
                    }
                });

                // Convert schedules to calendar format
                const formattedSchedules = schedulesResponse.data.map(schedule => {
                    const mappedEmployeeId = userIdToEmployeeId[schedule.user_id];
                    const shiftDate = schedule.shift_date.split('T')[0];
                    const startTime = moment(`${shiftDate} ${schedule.start_time}`);
                    const endTime = moment(`${shiftDate} ${schedule.end_time}`);
                    
                    return {
                        id: schedule.id,
                        calendarId: schedule.calendar_type || 'tasks',
                        title: schedule.title || 'Untitled Schedule',
                        start: startTime.toDate(),
                        end: endTime.toDate(),
                        resource: mappedEmployeeId ? String(mappedEmployeeId) : undefined
                    };
                });

                // Convert approved PTO requests to calendar events (only PTO counts for hours)
                const approvedRequests = requestsResponse.data.filter(request => 
                    request.status === 'approved' && request.is_pto === true
                );
                
                const requestEvents = [];
                approvedRequests.forEach(request => {
                    const mappedEmployeeId = userIdToEmployeeId[request.user_id];
                    if (!mappedEmployeeId) return;

                    // Generate events for each day in the request range
                    const startDate = moment(request.start_date);
                    const endDate = moment(request.end_date);
                    const currentDate = startDate.clone();

                    while (currentDate.isSameOrBefore(endDate, 'day')) {
                        // Create PTO event as 8-hour block (9am-5pm) - only PTO counts as paid hours
                        const eventStart = currentDate.clone().hour(9).minute(0).second(0);
                        const eventEnd = currentDate.clone().hour(17).minute(0).second(0);

                        requestEvents.push({
                            id: `request-${request.id}-${currentDate.format('YYYY-MM-DD')}`,
                            calendarId: 'pto', // Changed from 'time-off' to 'pto' to distinguish
                            title: `PTO - ${request.request_type.replace('_', ' ')}`,
                            start: eventStart.toDate(),
                            end: eventEnd.toDate(),
                            resource: String(mappedEmployeeId)
                        });

                        currentDate.add(1, 'day');
                    }
                });

                setEmployees(employeesResponse.data);
                // Combine schedules and request events
                const allEvents = [...formattedSchedules, ...requestEvents];
                setSchedules(allEvents);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError(error.response?.data?.error || 'Failed to fetch data');
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return <div className="hours-summary-page loading">Loading...</div>;
    }

    if (error) {
        return <div className="hours-summary-page error">Error: {error}</div>;
    }

    return (
        <div className="hours-summary-page">
            <div className="hours-summary-page-header">
                <button 
                    className="back-button"
                    onClick={() => navigate('/dashboard')}
                >
                    ← Back to Dashboard
                </button>
                <h1>Working Hours Summary</h1>
            </div>
            <div className="hours-summary-container">
                <HoursSummary 
                    employees={employees} 
                    schedules={schedules}
                />
            </div>
        </div>
    );
};

export default HoursSummaryPage; 